import { Button } from "@photo-booth/ui/button";
import { useEffectOnce } from "@/hooks/useEffectOnce";
import { useLoadingDots } from "@/hooks/useLoadingDots";
import { track } from "@/lib/analytics/track";
import { SOCIAL_LINE_URL } from "@/lib/constants/social";
import { MessageCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useCountdown, useUnmount } from "usehooks-ts";
import * as Sentry from "@sentry/react";

interface Props {
  isLoading: boolean;
}

export const LoadingPage: React.FC<Props> = ({ isLoading }) => {
  const loadingText = useLoadingDots({ baseText: "Loading", speed: 500 });
  const [searchParams] = useSearchParams();
  const [shouldShowLoadingErrorText, setShouldShowLoadingErrorText] =
    useState(false);
  const jobId = searchParams.get("jobId") ?? "";
  const [count, { startCountdown }] = useCountdown({
    countStart: 120,
    countStop: 0,
    intervalMs: 1000,
    isIncrement: false,
  });

  useEffect(() => {
    if (count === 0) {
      Sentry.logger.warn("Loading page timed out", {
        job_id: jobId,
      });
      setShouldShowLoadingErrorText(true);
    }
  }, [count, jobId]);

  useEffectOnce(() => {
    track("loading_start", {
      is_loading: true,
      timestamp: Date.now(),
      job_id: jobId,
    });
    startCountdown();
  });

  // Prevent body scrolling when loading
  useEffect(() => {
    if (isLoading) {
      // Disable scrolling on body
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      return () => {
        // Re-enable scrolling when component unmounts or loading finishes
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
      };
    }
  }, [isLoading, jobId]);

  useUnmount(() => {
    track("loading_end", {
      is_loading: false,
      timestamp: Date.now(),
      job_id: jobId,
    });
  });

  const handleClickContactUs = () => {
    window.open(SOCIAL_LINE_URL, "_blank");
  };

  return (
    <div className="bg-background h-screen-safe-area overflow-hidden fixed inset-0 w-full">
      <img
        src="/loading-animation/aug/loading.gif"
        alt=""
        loading="eager"
        className="w-full h-full object-cover z-0 absolute inset-0 "
        aria-hidden="true"
      />
      <p className="text-[40px] font-family-reenie z-10 absolute top-0 left-10/12">
        {count}
      </p>
      <div className="text-[40px] font-family-reenie z-10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full text-center px-12">
        {!shouldShowLoadingErrorText && <p className="mb-[30px]">{loadingText}</p>}
        {shouldShowLoadingErrorText && (
          <div className="flex flex-col items-center justify-center">
            <p className="text-center font-family-edmund text-vintage-red mt-8 text-[24px]">
              {`Something might went wrong for jobId: ${jobId}. Please contact us via line.`}
            </p>
            <Button
              onClick={handleClickContactUs}
              variant="outline"
              className="mt-6 border-vintage-red font-family-edmund text-vintage-red hover:bg-vintage-red hover:text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
            >
              <MessageCircleIcon className="w-4 h-4" />
              Contact Us
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
